import type { StoredVariant } from './media';

export type ImageKind = 'jpeg' | 'png' | 'gif' | 'webp' | 'avif' | 'heic';

/** Decoding more pixels than this can exhaust a tablet's memory: refused by the browser and by the Worker (P12a). */
export const MAX_IMAGE_PIXELS = 100_000_000;

const startsWith = (b: Uint8Array, sig: number[], at = 0) => sig.every((v, i) => b[at + i] === v);
const ascii = (b: Uint8Array, from: number, to: number) => String.fromCharCode(...b.subarray(from, to));
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'heim', 'heis', 'mif1', 'msf1']);

/** Magic bytes of the accepted formats (spec §10.1): JPEG, PNG, GIF, WebP, AVIF, HEIC. */
export function imageKind(b: Uint8Array): ImageKind | null {
  if (startsWith(b, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  if (ascii(b, 0, 6) === 'GIF87a' || ascii(b, 0, 6) === 'GIF89a') return 'gif';
  if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 12) === 'WEBP') return 'webp';
  if (ascii(b, 4, 8) === 'ftyp') {
    const brand = ascii(b, 8, 12);
    if (brand === 'avif' || brand === 'avis') return 'avif';
    if (HEIF_BRANDS.has(brand)) return 'heic';
  }
  return null;
}

const be16 = (b: Uint8Array, i: number) => ((b[i] ?? 0) << 8) | (b[i + 1] ?? 0);
const le16 = (b: Uint8Array, i: number) => (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8);
const le24 = (b: Uint8Array, i: number) => le16(b, i) | ((b[i + 2] ?? 0) << 16);
const be32 = (b: Uint8Array, i: number) => be16(b, i) * 65536 + be16(b, i + 2);

/**
 * Pixel size read from the header (first 64 KB), so an oversized image (a 1 × 1 file declaring 60 000 × 60 000) is
 * refused before anything decodes it. Null when the header does not say.
 */
export function imageDimensions(b: Uint8Array, kind: ImageKind | null = imageKind(b)): { w: number; h: number } | null {
  if (kind === 'png') return { w: be32(b, 16), h: be32(b, 20) };
  if (kind === 'gif') return { w: le16(b, 6), h: le16(b, 8) };
  if (kind === 'webp') {
    const chunk = ascii(b, 12, 16);
    if (chunk === 'VP8 ') return { w: le16(b, 26) & 0x3fff, h: le16(b, 28) & 0x3fff };
    if (chunk === 'VP8L') {
      const bits = (b[21] ?? 0) | ((b[22] ?? 0) << 8) | ((b[23] ?? 0) << 16) | ((b[24] ?? 0) << 24);
      return { w: (bits & 0x3fff) + 1, h: ((bits >>> 14) & 0x3fff) + 1 };
    }
    if (chunk === 'VP8X') return { w: le24(b, 24) + 1, h: le24(b, 27) + 1 };
    return null;
  }
  if (kind === 'jpeg') {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) {
        i++;
        continue;
      }
      const marker = b[i + 1]!;
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) return { w: be16(b, i + 7), h: be16(b, i + 5) };
      if (marker === 0xff) i++;
      else if ((marker >= 0xd0 && marker <= 0xd9) || marker === 0x01) i += 2;
      else i += 2 + be16(b, i + 2);
    }
  }
  return null;
}

/** Largest size of each stored variant: the long edge of a still variant, the 640 × 400 board thumbnail. */
const VARIANT_BOX: Record<Exclude<StoredVariant, 'original'>, { w: number; h: number }> = {
  '256': { w: 256, h: 256 },
  '1024': { w: 1024, h: 1024 },
  '2560': { w: 2560, h: 2560 },
  '640': { w: 640, h: 400 },
};

/**
 * True when an uploaded variant's own header says it fits what the client produces (P12a): a still variant within
 * its long edge, the thumbnail within 640 × 400, a kept GIF within MAX_IMAGE_PIXELS. Unreadable is refused.
 */
export function variantDimensionsOk(variant: StoredVariant, body: Uint8Array): boolean {
  const d = imageDimensions(body);
  if (!d || d.w < 1 || d.h < 1) return false;
  if (variant === 'original') return d.w * d.h <= MAX_IMAGE_PIXELS;
  const box = VARIANT_BOX[variant];
  return d.w <= box.w && d.h <= box.h;
}
